/**
 * App theme registry. The single place for built-in theme registration
 * and loading themes from the marketplace.
 */

import type { ThemeConfig } from "./types";
import {
  DEFAULT_THEME_ID,
  getInitialThemeId,
  getInstalledMarketplaceThemes,
  setInstalledMarketplaceThemes,
} from "./config";
import { logger } from "@/utils/logger";

const registry = new Map<string, ThemeConfig>();

export function registerTheme(theme: ThemeConfig): void {
  registry.set(theme.id, { ...theme, source: theme.source ?? "builtin" });
  logger.log(`Theme registered: ${theme.name} (${theme.id})`);
}

export function unregisterTheme(themeId: string): void {
  registry.delete(themeId);
}

export function getThemeById(themeId: string): ThemeConfig | undefined {
  return registry.get(themeId);
}

export function getDefaultTheme(): ThemeConfig {
  const id = getInitialThemeId();
  const resolved = registry.has(id) ? id : DEFAULT_THEME_ID;
  return registry.get(resolved) ?? registry.get(DEFAULT_THEME_ID) ?? Array.from(registry.values())[0]!;
}

export function getAllThemes(): ThemeConfig[] {
  return Array.from(registry.values());
}

export function getBuiltinThemes(): ThemeConfig[] {
  return getAllThemes().filter((t) => t.source !== "marketplace");
}

/**
 * Load a theme from the marketplace by bundle URL.
 * Expected export: { default: ThemeConfig } or { theme: ThemeConfig } or { createTheme, id, name, ... }
 */
export async function loadThemeFromMarketplace(bundleUrl: string): Promise<ThemeConfig> {
  const module = await import(/* webpackIgnore: true */ bundleUrl) as import("./types").MarketplaceThemeBundle;
  let config: ThemeConfig | undefined =
    module.default ?? module.theme;

  if (!config && (module.createTheme || module.id)) {
    config = {
      id: module.id ?? `marketplace-${Date.now()}`,
      name: module.name ?? "Marketplace Theme",
      description: module.description,
      createTheme: module.createTheme!,
      source: "marketplace",
      bundleUrl,
    };
  }

  if (!config?.createTheme) {
    throw new Error("Invalid theme bundle: missing default theme or createTheme");
  }

  const theme: ThemeConfig = {
    ...config,
    source: "marketplace",
    bundleUrl,
  };

  registerTheme(theme);

  const installed = getInstalledMarketplaceThemes();
  installed[theme.id] = { bundleUrl, installedAt: new Date().toISOString() };
  setInstalledMarketplaceThemes(installed);

  return theme;
}

/**
 * On application start, load themes installed from marketplace.
 */
export async function loadInstalledMarketplaceThemes(): Promise<void> {
  const installed = getInstalledMarketplaceThemes();
  for (const [themeId, { bundleUrl }] of Object.entries(installed)) {
    if (registry.has(themeId)) continue;
    try {
      await loadThemeFromMarketplace(bundleUrl);
    } catch (e) {
      logger.warn(`Failed to reload marketplace theme ${themeId}:`, e);
    }
  }
}

export function uninstallMarketplaceTheme(themeId: string): void {
  const installed = getInstalledMarketplaceThemes();
  delete installed[themeId];
  setInstalledMarketplaceThemes(installed);
  unregisterTheme(themeId);
}
